import { HubConnectionBuilder, LogLevel } from '@aspnet/signalr'
import axios from '@/util/axios';

const serverUrl = `${axios.defaults.baseURL}/hub/chat`;

const state = {
  senderId: null,
  isConnectionEstablished: false,
  sentMessages: [],
  receivedMessages: [],
  messagesToSend: [],
  uploadingFiles: [],
  newContacts: [],
  rejectedContactsIds: [],
  currentInterlocutorId: null,
  chatConfiguration: null,
  newMessagesCount: 0,
  newContactsCount: 0,
  invitedUsersIds: [],
  acceptedUsersIds: [],
};

var _isTryingToResend = false;
var _negativeId = 1;
var hubConnection = null;

const getters = {
  senderId: (state) => state.senderId,
  sentMessages: (state) => state.sentMessages,
  receivedMessages: (state) => state.receivedMessages,
  messagesToSend: (state) => state.messagesToSend,
  uploadingFiles: (state) => state.uploadingFiles,
  newContacts: (state) => state.newContacts,
  rejectedContactsIds: (state) => state.rejectedContactsIds,
  currentInterlocutorId: (state) => state.currentInterlocutorId,
  chatConfiguration: (state) => state.chatConfiguration,
  isConnectionEstablished: (state) => state.isConnectionEstablished,
  newMessagesCount: (state) => state.newMessagesCount,
  newContactsCount: (state) => state.newContactsCount,
  invitedUsersIds: (state) => state.invitedUsersIds,
  acceptedUsersIds: (state) => state.acceptedUsersIds,
}

const actions = {
  setSenderId({ commit }, senderId) {
    commit("setSenderId", senderId);
  },
  async openChatConnection({ state, dispatch, commit, rootGetters }) {
    try {
      if (hubConnection == null) {
        var token = rootGetters["user/token"];
        hubConnection = new HubConnectionBuilder()
          .withUrl(serverUrl, { accessTokenFactory: () => token })
          .configureLogging(LogLevel.Information)
          .build();

        hubConnection.on("ChatConfigurationReceived", function (configuration) {
          dispatch('handleChatConfigurationReceived', configuration);
        });
        hubConnection.on("AddChatContact", function (data) {
          dispatch('handleAddChatContact', data);
        });
        hubConnection.on("RemoveChatContact", function (data) {
          dispatch('handleRemoveChatContact', data);
        });
        hubConnection.on("MessageSent", function (message) {
          dispatch('handleSentMessage', message);
        });
        hubConnection.on("MessageReceived", function (message) {
          dispatch('handleMessage', message);
        });
        hubConnection.on("InvitationAccepted", function (configuration) {
          dispatch('handleInvitationAccepted', configuration);
        });
        hubConnection.onclose(function () {
          commit("setIsConnectionEstablished", false);
        });
      }

      // 1 = Connected, 2 = Connecting, 0 = Disconnected, 3 = Reconnecting
      if (hubConnection.state != 1) {
        await hubConnection.start();
        commit("setIsConnectionEstablished", true);
      }

      return true;
    } catch (exception) {
      commit("setIsConnectionEstablished", false);
      return false;
    }
  },
  async closeChatConnection({ commit }) {
    commit("clearSentMessages");
    commit("clearReceivedMessages");
    commit("clearMessagesToSend");
    commit("clearUploadingFiles");
    commit("clearNewContacts");
    commit("clearRejectedContactsIds");
    commit("clearConfiguration");
    commit("clearNewMessagesCount");

    if (hubConnection) {
      await hubConnection.stop();
    }
  },
  async invite({ commit, dispatch }, recipientId) {
    var openConnectionResult = await dispatch("openChatConnection");
    if (openConnectionResult) {
      await hubConnection.invoke("Invite", recipientId);

      commit("addInvitedUserId", recipientId);

      return true;
    }

    return false;
  },
  async rejectInvitation({ dispatch }, recipientId) {
    var openConnectionResult = await dispatch("openChatConnection");
    if (openConnectionResult) {
      await hubConnection.invoke("RejectInvitation", recipientId);
      return true;
    }

    return false;
  },
  async acceptInvitation({ dispatch }, recipientId) {
    var openConnectionResult = await dispatch("openChatConnection");
    if (openConnectionResult) {
      await hubConnection.invoke("AcceptInvitation", recipientId);
      return true;
    }

    return false;
  },
  async blockUser({ dispatch }, userId) {
    var openConnectionResult = await dispatch("openChatConnection");
    if (openConnectionResult) {
      await hubConnection.invoke("BlockUser", userId);
      return true;
    }

    return false;
  },
  async unblockUser({ dispatch }, userId) {
    var openConnectionResult = await dispatch("openChatConnection");
    if (openConnectionResult) {
      await hubConnection.invoke("UnblockUser", userId);
      return true;
    }

    return false;
  },
  async sendMessage({ state, dispatch }, payload) {
    
    if (state.messagesToSend.length > 0) {
      dispatch("addMessageToSend", payload);
      return;
    }

    var openConnectionResult = await dispatch("openChatConnection");
    if (openConnectionResult) {
      var argsObj = {
        'RecipientId': payload.recipientId,
        'Message': payload.message,
        'PanelId': payload.panelId
      };

      await hubConnection.invoke("Send", argsObj);
      return;
    }

    dispatch("addMessageToSend", {
      recipientId: recipientId,
      message: message,
    });

    if (!_isTryingToResend) {
      dispatch("sendRemainingMessages");
    }
  },
  async sendFile({ dispatch, state }, payload) {
    if (state.messagesToSend.length > 0) {
      dispatch("addMessageToSend", {
        recipientId: payload.recipientId,
        chatFile: {
          id: payload.fileId,
          filename: payload.filename,
          mimeType: payload.mimeType,
        },
      });
      return;
    }

    var openConnectionResult = await dispatch("openChatConnection");
    if (openConnectionResult) {
      var argsObj = {
        'RecipientId': payload.recipientId,
        'Message': payload.filename,
        'FileId': payload.fileId,
      };

      await hubConnection.invoke("Send", argsObj);
      return;
    }

    dispatch("addMessageToSend", {
      recipientId: payload.recipientId,
      chatFile: {
        id: payload.fileId,
        filename: payload.filename,
        mimeType: payload.mimeType,
      },
    });

    if (!_isTryingToResend) {
      dispatch("sendRemainingMessages");
    }
  },
  addSendingFile({ state, commit }, payload) {
    var file = {
      id: (_negativeId++) * -1,
      senderId: state.senderId,
      recipientId: payload.recipientId,
      message: payload.filename,
      sendDate: new Date(),
      fileId: null,
      isImage: payload.mimeType.includes("image"),
      isNew: false,
      isWaitingToSend: true
    };

    commit("pushNewUploadingFiles", file);
  },
  handleChatConfigurationReceived({ commit, dispatch }, configuration) {
    commit("setChatConfiguration", configuration);

    if (configuration.invitationSent) {
      commit("addInvitedUserId", configuration.interlocutorId);
      dispatch("calculateNewContacts");
    }
  },
  handleInvitationAccepted({ commit, dispatch }, configuration) {
    commit("addAcceptedUserId", configuration.interlocutorId);
    dispatch("calculateNewContacts");
  },
  handleAddChatContact({ commit, dispatch }, data) {
    commit("removeIdFromRejectedList", data.userId);
    commit("pushNewContact", data);
    dispatch("calculateNewContacts");
  },
  handleRemoveChatContact({ commit, dispatch }, data) {
    commit("removeFromNewContactsList", data);
    commit("pushRejectedId", data);
    commit("removeFromInvitedIds", data);
    dispatch("calculateNewContacts");
  },
  handleMessage({ commit, state, dispatch }, message) {
    message.isNew = !(message.senderId == state.senderId || message.senderId == state.currentInterlocutorId);
    message.message = message.message ?? message.filename;
    message.isImage = message?.fileMimeType?.includes("image") ?? false;

    commit("pushNewReceivedMessages", message);
    dispatch("calculateNewMessages");
  },
  handleSentMessage({ commit, state }, message) {
    message.isNew = !(message.senderId == state.senderId || message.senderId == state.currentInterlocutorId);
    message.message = message.message ?? message.filename;
    message.isImage = message?.fileMimeType?.includes("image") ?? false;

    if (message.fileId != undefined) {
      var firstUploading = state.uploadingFiles.filter(function (item) {
        return item['message'] == message.message;
      })[0];

      if (firstUploading != undefined) {
        commit("removeFromUploadingFiles", firstUploading);
      }
    }

    commit("pushNewSentMessage", message);
  },
  addMessageToSend({ commit, state }, payload) {
    var message = {
      id: (_negativeId++) * -1,
      senderId: state.senderId,
      recipientId: payload.recipientId,
      message: payload.message ?? payload.chatFile?.filename,
      panelId: payload.panel?.id,
      sendDate: new Date(),
      fileId: payload.chatFile?.id,
      isImage: payload.chatFile?.mimeType?.includes("image") ?? false,
      isNew: false,
      isWaitingToSend: true,
      mimeType: payload.chatFile?.mimeType
    };

    if (payload.chatFile != undefined) {
      var firstUploading = state.uploadingFiles.filter(function (item) {
        return item['message'] == message.message && item['recipientId'] == payload.recipientId;
      })[0];

      if (firstUploading != undefined) {
        commit("removeFromUploadingFiles", firstUploading);
      }
    }

    commit("pushNewMessagesToSend", message);
  },
  async sendRemainingMessages({ commit, state, dispatch }) {
    _isTryingToResend = true;

    if (state.messagesToSend.length == 0) {
      _isTryingToResend = false;
      return;
    }

    var openConnectionResult = await dispatch("openChatConnection");
    if (openConnectionResult) {
      var firstToSend = state.messagesToSend[0];
      var recipientId = firstToSend.recipientId;
      var message = firstToSend.message;
      var fileId = firstToSend.fileId;

      var argsObj = {
        'RecipientId': recipientId,
        'Message': message,
      };

      if (fileId != undefined) {
        argsObj['fileId'] = fileId;
      }

      await hubConnection.invoke("Send", argsObj);

      setTimeout(function () {
        commit("shiftMessagesToSend");
        dispatch("sendRemainingMessages");
      }, 1000);
    } else {
      setTimeout(function () {
        dispatch("sendRemainingMessages");
      }, 5000);
    }
  },
  async setInterlocutorMessagesAsRead({ commit, state }, interlocutorId) {
    for (var i = 0; i < state.receivedMessages.length; ++i) {
      if (state.receivedMessages[i].senderId == interlocutorId) {
        await commit("setMessageAtIndexAsRead", i);
      }
    }

    var count = state.receivedMessages.filter(function (item) {
      return item.isNew;
    }).length;

    commit("setNewMessagesCount", count);
  },
  calculateNewMessages({ state, commit }) {
    var count = state.receivedMessages.filter(function (item) {
      return item.isNew;
    }).length;

    commit("setNewMessagesCount", count);
  },
  calculateNewContacts({ state, commit }) {
    var acceptedIds = state.acceptedUsersIds;
    var invitedIds = state.invitedUsersIds;

    var count = state.newContacts.filter(function (item) {
      var temp1 = acceptedIds.some(function (id) {
        return id == item.userId;
      });
      var temp2 = invitedIds.some(function (id) {
        return id == item.userId;
      });

      return !temp1 && !temp2;
    }).length;

    commit("setNewContactsCount", count);
  },
}

const mutations = {
  setSenderId(state, value) {
    state.senderId = value;
  },
  setIsConnectionEstablished(state, value) {
    state.isConnectionEstablished = value;
  },
  setCurrentInterlocutorId(state, payload) {
    state.currentInterlocutorId = payload;
  },
  setChatConfiguration(state, value) {
    state.chatConfiguration = value;
  },
  setNewMessagesCount(state, value) {
    state.newMessagesCount = value;
  },
  setNewContactsCount(state, value) {
    state.newContactsCount = value;
  },
  setMessageAtIndexAsRead(state, value) {
    state.receivedMessages[value].isNew = false;
  },
  addAcceptedUserId(state, value) {
    var hasValue = state.acceptedUsersIds.some(function (id) {
      return id == value;
    });

    if (!hasValue) {
      state.acceptedUsersIds.push(value);
    }
  },
  addInvitedUserId(state, value) {
    var hasValue = state.invitedUsersIds.some(function (id) {
      return id == value;
    });

    if (!hasValue) {
      state.invitedUsersIds.push(value);
    }
  },
  pushNewSentMessage(state, message) {
    state.sentMessages.push(message);
  },
  pushNewReceivedMessages(state, message) {
    state.receivedMessages.push(message);
  },
  pushNewMessagesToSend(state, message) {
    state.messagesToSend.push(message);
  },
  pushNewUploadingFiles(state, message) {
    state.uploadingFiles.push(message);
  },
  pushNewContact(state, contact) {
    state.newContacts.push(contact);
  },
  pushRejectedId(state, id) {
    state.rejectedContactsIds.push(id);
  },
  removeFromUploadingFiles(state, firstUploading) {
    state.uploadingFiles = state.uploadingFiles.filter(function (item) {
      return item != firstUploading;
    });
  },
  removeIdFromRejectedList(state, id) {
    state.rejectedContactsIds = state.rejectedContactsIds.filter(function (rejectedId) {
      return rejectedId != id;
    });
  },
  removeFromNewContactsList(state, id) {
    state.newContacts = state.newContacts.filter(function (contact) {
      return contact.userId != id;
    });
  },
  removeFromInvitedIds(state, id) {
    state.invitedUsersIds = state.invitedUsersIds.filter(function (item) {
      return item != id;
    });
  },
  shiftMessagesToSend(state) {
    state.messagesToSend.shift();
  },
  clearSentMessages(state) {
    state.sentMessages = [];
  },
  clearReceivedMessages(state) {
    state.receivedMessages = [];
  },
  clearMessagesToSend(state) {
    state.messagesToSend = [];
  },
  clearUploadingFiles(state) {
    state.uploadingFiles = [];
  },
  clearNewContacts(state) {
    state.newContacts = [];
  },
  clearRejectedContactsIds(state) {
    state.rejectedContactsIds = [];
  },
  clearConfiguration(state) {
    state.chatConfiguration = null;
  },
  clearNewMessagesCount(state) {
    state.newMessagesCount = 0;
  }
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
}
